/**
 * 워크플로우 로그 페이지
 * 
 * 사용자 검색 워크플로우를 실시간으로 추적하고 분석
 * 
 * @version 10.23
 * @date 2025-12-19
 */

import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Container,
  Heading,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  Button,
  useToast,
  Spinner,
  Text,
  HStack,
  VStack,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  useDisclosure,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
  Code,
  Divider,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  SimpleGrid,
  Card,
  CardBody,
  useColorModeValue,
  IconButton,
  Tooltip,
  Select
} from '@chakra-ui/react';
import { RepeatIcon, ChevronLeftIcon, ChevronRightIcon } from '@chakra-ui/icons';
import axios from 'axios';

interface WorkflowSummary {
  id: number;
  session_id: string;
  user_id: string | null;
  query: string;
  status: string;
  answer: string | null;
  confidence: number | null;
  total_steps: number;
  total_processing_time_ms: number;
  total_tokens: number;
  created_at: string;
  completed_at: string | null;
}

interface WorkflowStep {
  id: number;
  session_id: string;
  step: string;
  step_order: number;
  step_status: string;
  step_data: any;
  search_results_count: number;
  search_scores: number[] | null;
  search_keywords: string[] | null;
  context_length: number;
  context_chunks_count: number;
  context_preview: string | null;
  answer: string | null;
  confidence: number | null;
  processing_time_ms: number;
  token_usage: any;
  error_message: string | null;
  created_at: string;
}

interface WorkflowDetail {
  summary: WorkflowSummary;
  steps: WorkflowStep[];
}

const WorkflowLogsPage: React.FC = () => {
  const [workflows, setWorkflows] = useState<WorkflowSummary[]>([]);
  const [selectedWorkflow, setSelectedWorkflow] = useState<WorkflowDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const toast = useToast();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  
  // 페이징 상태
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [pageSize, setPageSize] = useState(20); // 기본 20개로 변경
  
  // 🎨 새 워크플로우 애니메이션 상태
  const [newWorkflowIds, setNewWorkflowIds] = useState<Set<number>>(new Set());
  const allSeenIdsRef = useRef<Set<number>>(new Set()); // useRef로 변경 (클로저 문제 해결)

  // 워크플로우 목록 로드
  const loadWorkflows = async (checkNew: boolean = false) => {
    console.log('🔍 loadWorkflows 호출:', { checkNew, allSeenIdsSize: allSeenIdsRef.current.size });
    setLoading(true);
    try {
      const response = await axios.get(`/api/workflows/recent?limit=${pageSize}&page=${currentPage}`);
      if (response.data.success) {
        const newData = response.data.data;
        const incomingIds = newData.map((w: WorkflowSummary) => w.id);
        
        console.log('📥 받은 데이터:', { count: incomingIds.length, ids: incomingIds.slice(0, 3) });
        
        // 🎨 새로운 워크플로우 감지
        if (checkNew && allSeenIdsRef.current.size > 0) {
          const newIds = incomingIds.filter(id => !allSeenIdsRef.current.has(id));
          
          console.log('🎨 새 워크플로우 체크:', { 
            allSeenIds: Array.from(allSeenIdsRef.current).slice(0, 3),
            newIds 
          });
          
          if (newIds.length > 0) {
            console.log('✨ 애니메이션 표시:', newIds);
            setNewWorkflowIds(new Set(newIds));
            // 3초 후 애니메이션 제거
            setTimeout(() => {
              console.log('🔚 애니메이션 제거');
              setNewWorkflowIds(new Set());
            }, 3000);
          }
        }
        
        // 모든 ID를 allSeenIdsRef에 추가
        incomingIds.forEach(id => allSeenIdsRef.current.add(id));
        console.log('💾 저장된 ID 개수:', allSeenIdsRef.current.size);
        
        setWorkflows(newData);
        
        if (response.data.pagination) {
          setTotalPages(response.data.pagination.totalPages);
          setTotalItems(response.data.pagination.totalItems);
        }
      }
    } catch (error: any) {
      toast({
        title: '워크플로우 목록 로드 실패',
        description: error.response?.data?.message || error.message,
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  // 워크플로우 상세 로드
  const loadWorkflowDetail = async (sessionId: string) => {
    setDetailLoading(true);
    try {
      const response = await axios.get(`/api/workflows/${sessionId}`);
      if (response.data.success) {
        // 🔧 JSON 문자열을 파싱
        const data = response.data.data;
        if (data.steps) {
          data.steps = data.steps.map((step: any) => {
            // JSON 문자열 파싱
            const parsedStep = {
              ...step,
              search_keywords: step.search_keywords ? 
                (typeof step.search_keywords === 'string' ? JSON.parse(step.search_keywords) : step.search_keywords) : 
                null,
              search_scores: step.search_scores ? 
                (typeof step.search_scores === 'string' ? JSON.parse(step.search_scores) : step.search_scores) : 
                null,
              token_usage: step.token_usage ? 
                (typeof step.token_usage === 'string' ? JSON.parse(step.token_usage) : step.token_usage) : 
                null,
            };

            // 🔧 token_usage 필드 이름 매핑 (inputTokens → input, outputTokens → output)
            if (parsedStep.token_usage) {
              parsedStep.token_usage = {
                input: parsedStep.token_usage.inputTokens || parsedStep.token_usage.input || 0,
                output: parsedStep.token_usage.outputTokens || parsedStep.token_usage.output || 0,
                total: parsedStep.token_usage.totalTokens || parsedStep.token_usage.total || 0,
              };
            }

            return parsedStep;
          });
        }
        setSelectedWorkflow(data);
        onOpen();
      }
    } catch (error: any) {
      toast({
        title: '워크플로우 상세 로드 실패',
        description: error.response?.data?.message || error.message,
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setDetailLoading(false);
    }
  };

  useEffect(() => {
    // 페이징 변경 시에는 애니메이션 체크 안 함
    loadWorkflows(false);
  }, [currentPage, pageSize]);
  
  useEffect(() => {
    // 10초마다 자동 새로고침 (애니메이션 체크)
    const interval = setInterval(() => loadWorkflows(true), 10000);
    return () => clearInterval(interval);
  }, [currentPage, pageSize]);

  // 상태 뱃지 색상
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'success': return 'green';
      case 'error': return 'red';
      case 'processing': return 'yellow';
      default: return 'gray';
    }
  };

  // 단계 뱃지 색상
  const getStepColor = (step: string) => {
    switch (step) {
      case 'agent': return 'purple';
      case 'semantic_check': return 'blue';
      case 'vector_search': return 'cyan';
      case 'context_prepare': return 'teal';
      case 'llm_generate': return 'orange';
      case 'done': return 'green';
      default: return 'gray';
    }
  };

  // 단계 이름 한글화
  const getStepName = (step: string) => {
    const names: Record<string, string> = {
      'agent': '1단계: 질문 분석',
      'semantic_check': '2단계: 유효성 검증',
      'vector_search': '3단계: 문서 검색',
      'context_prepare': '4단계: 답변 준비',
      'llm_generate': '5단계: 답변 생성',
      'done': '완료'
    };
    return names[step] || step;
  };

  // 단계별 설명
  const getStepDescription = (step: string) => {
    const descriptions: Record<string, string> = {
      'agent': '사용자 질문을 분석하고 이전 대화 맥락을 파악합니다',
      'semantic_check': '질문이 유효한지 확인합니다 (존재하지 않는 시설/층수 차단)',
      'vector_search': 'ChromaDB에서 관련 문서를 검색합니다',
      'context_prepare': '검색된 문서를 LLM이 이해할 수 있도록 정리합니다',
      'llm_generate': 'AI가 최종 답변을 생성합니다',
      'done': '모든 단계가 완료되었습니다'
    };
    return descriptions[step] || '';
  };

  // 날짜 포맷 (검증 리포트와 동일)
  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString('ko-KR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // 페이지 변경 핸들러
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  // 페이지 번호 계산 (최대 5개 표시)
  const getPageNumbers = () => {
    const pages: (number | string)[] = [];
    const maxPagesToShow = 5;
    
    if (totalPages <= maxPagesToShow) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      let startPage = Math.max(1, currentPage - 2);
      let endPage = Math.min(totalPages, currentPage + 2);
      
      if (startPage > 1) {
        pages.push(1);
        if (startPage > 2) {
          pages.push('...');
        }
      }
      
      for (let i = startPage; i <= endPage; i++) {
        pages.push(i);
      }
      
      if (endPage < totalPages) {
        if (endPage < totalPages - 1) {
          pages.push('...');
        }
        pages.push(totalPages);
      }
    }
    
    return pages;
  };

  return (
    <Box p={6}>
      <VStack spacing={6} align="stretch">
        {/* 헤더 */}
        <HStack justify="space-between" flexWrap="wrap" gap={4}>
          <Box>
            <Heading size="lg" color="bexco.700">🔍 검색 워크플로우 로그</Heading>
            <Text color="gray.500" mt={1}>사용자 검색 워크플로우를 실시간으로 추적하고 분석 (자동 새로고침: 10초)</Text>
          </Box>
          
          <IconButton
            aria-label="새로고침"
            icon={<RepeatIcon />}
            size="sm"
            colorScheme="blue"
            onClick={() => loadWorkflows(true)}
            isLoading={loading}
          />
        </HStack>

        {/* 워크플로우 목록 */}
        <Card bg={cardBg} borderColor={borderColor} borderWidth="1px">
          <CardBody>
            {loading && workflows.length === 0 ? (
              <Box textAlign="center" py={10}>
                <Spinner size="lg" color="bexco.500" />
              </Box>
            ) : workflows.length === 0 ? (
              <Box textAlign="center" py={10} color="gray.500">
                <Text>워크플로우 로그가 없습니다.</Text>
              </Box>
            ) : (
              <Box overflowX="auto">
                <Table variant="simple" size="sm">
                  <Thead>
                    <Tr>
                      <Th>생성일</Th>
                      <Th>쿼리</Th>
                      <Th>상태</Th>
                      <Th isNumeric>신뢰도</Th>
                      <Th isNumeric>단계</Th>
                      <Th isNumeric>처리시간</Th>
                      <Th isNumeric>토큰</Th>
                      <Th>작업</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {workflows.map((workflow) => {
                      const isNew = newWorkflowIds.has(workflow.id);
                      return (
                        <Tr 
                          key={workflow.id} 
                          _hover={{ bg: 'gray.50' }}
                          position="relative"
                          sx={isNew ? {
                            animation: 'newRowPulse 3s ease-out',
                            '@keyframes newRowPulse': {
                              '0%': {
                                backgroundColor: 'rgba(49, 151, 149, 0.3)',
                                boxShadow: '0 0 0 0 rgba(49, 151, 149, 0.7), inset 0 0 0 3px rgba(49, 151, 149, 0.7)',
                              },
                              '50%': {
                                backgroundColor: 'rgba(49, 151, 149, 0.15)',
                                boxShadow: '0 0 0 4px rgba(49, 151, 149, 0.4), inset 0 0 0 3px rgba(49, 151, 149, 0.4)',
                              },
                              '100%': {
                                backgroundColor: 'transparent',
                                boxShadow: '0 0 0 0 rgba(49, 151, 149, 0), inset 0 0 0 0 rgba(49, 151, 149, 0)',
                              }
                            }
                          } : {}}
                        >
                          <Td>
                            <Text fontSize="sm">
                              {formatDate(workflow.created_at)}
                            </Text>
                          </Td>
                        <Td maxW="300px">
                          <Tooltip label="클릭하여 상세 보기">
                            <Text 
                              isTruncated 
                              cursor="pointer" 
                              color="blue.500"
                              fontWeight="medium"
                              fontSize="sm"
                              _hover={{ textDecoration: 'underline' }}
                              onClick={() => loadWorkflowDetail(workflow.session_id)}
                            >
                              {workflow.query}
                            </Text>
                          </Tooltip>
                        </Td>
                        <Td>
                          <Badge colorScheme={getStatusColor(workflow.status)}>
                            {workflow.status}
                          </Badge>
                        </Td>
                        <Td isNumeric>
                          {workflow.confidence !== null ? (
                            <Badge colorScheme={
                              workflow.confidence >= 0.8 ? 'green' :
                              workflow.confidence >= 0.5 ? 'yellow' : 'red'
                            }>
                              {(workflow.confidence * 100).toFixed(1)}%
                            </Badge>
                          ) : (
                            <Text color="gray.400">-</Text>
                          )}
                        </Td>
                        <Td isNumeric>
                          <Badge>{workflow.total_steps}</Badge>
                        </Td>
                        <Td isNumeric>
                          <Text fontSize="sm">{workflow.total_processing_time_ms}ms</Text>
                        </Td>
                        <Td isNumeric>
                          <Text fontSize="sm">{workflow.total_tokens || 0}</Text>
                        </Td>
                        <Td>
                          <Button
                            size="xs"
                            colorScheme="blue"
                            onClick={() => loadWorkflowDetail(workflow.session_id)}
                            isLoading={detailLoading}
                          >
                            상세
                          </Button>
                        </Td>
                      </Tr>
                    );
                    })}
                  </Tbody>
                </Table>
                
                {/* 페이징 */}
                {workflows.length > 0 && (
                  <HStack justify="space-between" mt={4} flexWrap="wrap" gap={3}>
                    <HStack spacing={2}>
                      <Text fontSize="sm" color="gray.600">
                        페이지당:
                      </Text>
                      <Select
                        value={pageSize}
                        onChange={(e) => {
                          setPageSize(Number(e.target.value));
                          setCurrentPage(1);
                        }}
                        size="sm"
                        width="80px"
                      >
                        <option value={10}>10</option>
                        <option value={20}>20</option>
                        <option value={50}>50</option>
                      </Select>
                      <Text fontSize="sm" color="gray.500">
                        총 {totalItems}개
                      </Text>
                    </HStack>
                    
                    {totalPages > 1 && (
                      <HStack spacing={1}>
                        {/* 처음 */}
                        <Button
                          size="sm"
                          variant="outline"
                          colorScheme="blue"
                          isDisabled={currentPage === 1}
                          onClick={() => handlePageChange(1)}
                          minW="40px"
                        >
                          ««
                        </Button>
                        {/* 이전 */}
                        <IconButton
                          aria-label="이전 페이지"
                          icon={<ChevronLeftIcon />}
                          size="sm"
                          variant="outline"
                          colorScheme="blue"
                          isDisabled={currentPage === 1}
                          onClick={() => handlePageChange(currentPage - 1)}
                        />
                        {/* 페이지 번호 */}
                        {getPageNumbers().map((page, index) => {
                          if (page === '...') {
                            return (
                              <Text key={`ellipsis-${index}`} fontSize="sm" px={2} color="gray.500">
                                ...
                              </Text>
                            );
                          }
                          const pageNum = page as number;
                          const isActive = pageNum === currentPage;
                          return (
                            <Button
                              key={pageNum}
                              size="sm"
                              variant={isActive ? 'solid' : 'outline'}
                              colorScheme="blue"
                              onClick={() => handlePageChange(pageNum)}
                              minW="40px"
                            >
                              {pageNum}
                            </Button>
                          );
                        })}
                        {/* 다음 */}
                        <IconButton
                          aria-label="다음 페이지"
                          icon={<ChevronRightIcon />}
                          size="sm"
                          variant="outline"
                          colorScheme="blue"
                          isDisabled={currentPage === totalPages}
                          onClick={() => handlePageChange(currentPage + 1)}
                        />
                        {/* 마지막 */}
                        <Button
                          size="sm"
                          variant="outline"
                          colorScheme="blue"
                          isDisabled={currentPage === totalPages}
                          onClick={() => handlePageChange(totalPages)}
                          minW="40px"
                        >
                          »»
                        </Button>
                      </HStack>
                    )}
                  </HStack>
                )}
              </Box>
            )}
          </CardBody>
        </Card>
      </VStack>

      {/* 상세 모달 */}
      <Modal isOpen={isOpen} onClose={onClose} size="6xl" scrollBehavior="inside">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>워크플로우 상세</ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            {selectedWorkflow && (
              <VStack spacing={6} align="stretch">
                {/* 요약 정보 */}
                <Box bg="gray.50" p={6} borderRadius="lg">
                  <Heading size="md" mb={4}>📊 검색 요약</Heading>
                  <VStack align="stretch" spacing={4}>
                    <Box>
                      <Text fontSize="sm" color="gray.600" mb={1}>사용자 질문</Text>
                      <Text fontSize="lg" fontWeight="bold">{selectedWorkflow.summary.query}</Text>
                    </Box>
                    <SimpleGrid columns={[1, 2, 4]} spacing={4}>
                      <Stat>
                        <StatLabel>처리 상태</StatLabel>
                        <StatNumber>
                          <Badge colorScheme={getStatusColor(selectedWorkflow.summary.status)} fontSize="md">
                            {selectedWorkflow.summary.status === 'success' ? '✅ 완료' : 
                             selectedWorkflow.summary.status === 'error' ? '❌ 오류' : '⏳ 처리중'}
                          </Badge>
                        </StatNumber>
                      </Stat>
                      <Stat>
                        <StatLabel>답변 신뢰도</StatLabel>
                        <StatNumber>
                          {selectedWorkflow.summary.confidence !== null ? (
                            <Badge 
                              colorScheme={
                                selectedWorkflow.summary.confidence >= 0.8 ? 'green' :
                                selectedWorkflow.summary.confidence >= 0.5 ? 'yellow' : 'red'
                              } 
                              fontSize="md"
                            >
                              {(selectedWorkflow.summary.confidence * 100).toFixed(1)}%
                            </Badge>
                          ) : (
                            <Text color="gray.400">-</Text>
                          )}
                        </StatNumber>
                        <StatHelpText>
                          {selectedWorkflow.summary.confidence !== null && selectedWorkflow.summary.confidence >= 0.8 ? '매우 정확' :
                           selectedWorkflow.summary.confidence !== null && selectedWorkflow.summary.confidence >= 0.5 ? '참고용' : '확인 필요'}
                        </StatHelpText>
                      </Stat>
                      <Stat>
                        <StatLabel>총 처리시간</StatLabel>
                        <StatNumber>{(selectedWorkflow.summary.total_processing_time_ms / 1000).toFixed(2)}초</StatNumber>
                        <StatHelpText>{selectedWorkflow.summary.total_processing_time_ms}ms</StatHelpText>
                      </Stat>
                      <Stat>
                        <StatLabel>사용 토큰</StatLabel>
                        <StatNumber>{selectedWorkflow.summary.total_tokens?.toLocaleString() || 0}</StatNumber>
                        <StatHelpText>입력 + 출력</StatHelpText>
                      </Stat>
                    </SimpleGrid>
                  </VStack>
                </Box>

                <Divider />

                {/* 단계별 로그 */}
                <Box>
                  <Heading size="md" mb={4}>📝 단계별 로그</Heading>
                  <Accordion allowMultiple>
                    {selectedWorkflow.steps
                      .filter(step => {
                        // 🔧 의미있는 내용이 있는 단계만 표시
                        // vector_search, context_prepare, llm_generate 단계만 표시
                        if (!['vector_search', 'context_prepare', 'llm_generate'].includes(step.step)) {
                          return false;
                        }
                        
                        // 🔧 vector_search 단계 중 'processing' 상태(검색 시작)는 표시하지 않음
                        // 'success' 상태(검색 완료)만 표시
                        if (step.step === 'vector_search' && step.step_status === 'processing') {
                          return false;
                        }
                        
                        return true;
                      })
                      .map((step, index) => (
                      <AccordionItem key={step.id}>
                        <AccordionButton>
                          <Box flex="1" textAlign="left">
                            <VStack align="stretch" spacing={1}>
                              <HStack>
                                <Badge colorScheme={getStepColor(step.step)} fontSize="sm">
                                  {step.step_order}
                                </Badge>
                                <Text fontWeight="bold" fontSize="md">{getStepName(step.step)}</Text>
                                <Badge colorScheme={getStatusColor(step.step_status)}>
                                  {step.step_status === 'success' ? '완료' : step.step_status === 'processing' ? '처리중' : '오류'}
                                </Badge>
                                {/* 🔧 처리 시간이 0ms이면 표시하지 않음 */}
                                {step.processing_time_ms > 0 && (
                                  <Text fontSize="sm" color="gray.500">
                                    {step.processing_time_ms}ms
                                  </Text>
                                )}
                              </HStack>
                              <Text fontSize="sm" color="gray.600">
                                {getStepDescription(step.step)}
                              </Text>
                            </VStack>
                          </Box>
                          <AccordionIcon />
                        </AccordionButton>
                        <AccordionPanel pb={4}>
                          <VStack align="stretch" spacing={4}>
                            {/* 3단계: 문서 검색 */}
                            {step.step === 'vector_search' && step.search_results_count > 0 && (
                              <Box bg="blue.50" p={4} borderRadius="md">
                                <Text fontWeight="bold" mb={3} fontSize="md">📊 검색 단계별 문서 수</Text>
                                <VStack align="stretch" spacing={2}>
                                  <HStack justify="space-between">
                                    <Text fontSize="sm">1️⃣ ChromaDB 초기 검색</Text>
                                    <Badge colorScheme="blue" fontSize="sm">200개 요청</Badge>
                                  </HStack>
                                  <HStack justify="space-between">
                                    <Text fontSize="sm">2️⃣ 시설 청크 추가</Text>
                                    <Badge colorScheme="cyan" fontSize="sm">+15개 (예상)</Badge>
                                  </HStack>
                                  <HStack justify="space-between">
                                    <Text fontSize="sm">3️⃣ 키워드 매칭 추가</Text>
                                    <Badge colorScheme="cyan" fontSize="sm">+15개 (예상)</Badge>
                                  </HStack>
                                  <HStack justify="space-between">
                                    <Text fontSize="sm">4️⃣ Webpage 수집</Text>
                                    <Badge colorScheme="purple" fontSize="sm">최대 50개</Badge>
                                  </HStack>
                                  <HStack justify="space-between">
                                    <Text fontSize="sm">5️⃣ Webpage 필터링</Text>
                                    <Badge colorScheme="orange" fontSize="sm">타겟 20개 + 나머지 10개</Badge>
                                  </HStack>
                                  <Divider my={2} />
                                  <HStack justify="space-between">
                                    <Text fontWeight="bold" fontSize="md">✅ 최종 검색 결과</Text>
                                    <Badge colorScheme="green" fontSize="md">{step.search_results_count}개</Badge>
                                  </HStack>
                                  {step.search_keywords && step.search_keywords.length > 0 && (
                                    <Box mt={2}>
                                      <Text fontSize="sm" fontWeight="bold" mb={1}>🔑 추출된 키워드</Text>
                                      <HStack flexWrap="wrap" spacing={1}>
                                        {step.search_keywords.map((kw, idx) => (
                                          <Badge key={idx} colorScheme="purple" fontSize="xs">{kw}</Badge>
                                        ))}
                                      </HStack>
                                    </Box>
                                  )}
                                </VStack>
                              </Box>
                            )}

                            {/* 4단계: 답변 준비 */}
                            {step.step === 'context_prepare' && step.context_length > 0 && (() => {
                              // 🔧 5단계(llm_generate)에서 실제 입력 토큰 찾기
                              const llmStep = selectedWorkflow.steps.find(s => s.step === 'llm_generate');
                              const actualInputTokens = llmStep?.token_usage?.input || null;
                              
                              return (
                                <Box bg="teal.50" p={4} borderRadius="md">
                                  <Text fontWeight="bold" mb={3} fontSize="md">📄 AI에게 전달할 정보</Text>
                                  <SimpleGrid columns={[1, 2]} spacing={4}>
                                    <Stat size="sm">
                                      <StatLabel>전달 문서 수</StatLabel>
                                      <StatNumber fontSize="lg">{step.context_chunks_count}개</StatNumber>
                                      <StatHelpText>검색된 문서 중 선별</StatHelpText>
                                    </Stat>
                                    <Stat size="sm">
                                      <StatLabel>전달 텍스트 길이</StatLabel>
                                      <StatNumber fontSize="lg">{step.context_length.toLocaleString()}자</StatNumber>
                                      <StatHelpText>
                                        {actualInputTokens 
                                          ? `약 ${actualInputTokens.toLocaleString()} 토큰 (실제 입력)`
                                          : `약 ${Math.round(step.context_length / 0.4).toLocaleString()} 토큰 (추정)`
                                        }
                                      </StatHelpText>
                                    </Stat>
                                  </SimpleGrid>
                                </Box>
                              );
                            })()}

                            {/* 5단계: 답변 생성 */}
                            {step.step === 'llm_generate' && step.token_usage && (
                              <Box bg="orange.50" p={4} borderRadius="md">
                                <Text fontWeight="bold" mb={3} fontSize="md">🤖 AI 답변 생성</Text>
                                <SimpleGrid columns={[1, 3]} spacing={4}>
                                  <Stat size="sm">
                                    <StatLabel>입력 토큰</StatLabel>
                                    <StatNumber fontSize="lg">{step.token_usage.input?.toLocaleString() || 0}</StatNumber>
                                    <StatHelpText>AI가 읽은 양</StatHelpText>
                                  </Stat>
                                  <Stat size="sm">
                                    <StatLabel>출력 토큰</StatLabel>
                                    <StatNumber fontSize="lg">{step.token_usage.output?.toLocaleString() || 0}</StatNumber>
                                    <StatHelpText>AI가 생성한 양</StatHelpText>
                                  </Stat>
                                  <Stat size="sm">
                                    <StatLabel>처리 시간</StatLabel>
                                    <StatNumber fontSize="lg">{step.processing_time_ms}ms</StatNumber>
                                    <StatHelpText>{(step.processing_time_ms / 1000).toFixed(1)}초</StatHelpText>
                                  </Stat>
                                </SimpleGrid>
                              </Box>
                            )}

                            {/* 오류 메시지 */}
                            {step.error_message && (
                              <Box bg="red.50" p={4} borderRadius="md" borderLeft="4px solid" borderColor="red.500">
                                <Text fontWeight="bold" color="red.600" mb={2}>❌ 오류 발생</Text>
                                <Text fontSize="sm" color="red.700">{step.error_message}</Text>
                              </Box>
                            )}
                          </VStack>
                        </AccordionPanel>
                      </AccordionItem>
                    ))}
                  </Accordion>
                </Box>

                {/* 최종 답변 */}
                {selectedWorkflow.summary.answer && (
                  <>
                    <Divider />
                    <Box>
                      <Heading size="md" mb={4}>💬 최종 답변</Heading>
                      <Box bg="gray.50" p={4} borderRadius="md">
                        <div dangerouslySetInnerHTML={{ __html: selectedWorkflow.summary.answer }} />
                      </Box>
                    </Box>
                  </>
                )}
              </VStack>
            )}
          </ModalBody>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default WorkflowLogsPage;

